import {
  RadioCard as ChakraRadioCard,
  RadioCardItemProps,
  SimpleGrid,
  SimpleGridProps,
} from '@chakra-ui/react'
import { Text } from '.'

interface RadioCardsProps<T>
  extends Omit<SimpleGridProps, 'children' | 'value' | 'onChange' | 'onValueChange'> {
  options: RadioCardOption[]
  value: T
  onChange?: (value: T) => void
  isVertical?: boolean
}

export interface RadioCardOption {
  label: string
  value: string
  description?: string
  isDisabled?: boolean
  icon?: JSX.Element
}

export default function RadioCards<T extends string>({
  options,
  value,
  isVertical,
  onChange,
  ...rest
}: RadioCardsProps<T>) {
  return (
    <ChakraRadioCard.Root value={value} onValueChange={p => onChange?.(p.value as T)}>
      <SimpleGrid columns={isVertical ? 1 : options.length} gap={3} {...rest}>
        {options.map(p => (
          <RadioCard key={p.value} {...p} />
        ))}
      </SimpleGrid>
    </ChakraRadioCard.Root>
  )
}

interface RadioCardProps extends Omit<RadioCardItemProps, 'children'> {
  label: string
  description?: string
  isDisabled?: boolean
  icon?: JSX.Element
}

export function RadioCard({ label, description, isDisabled, icon, ...rest }: RadioCardProps) {
  return (
    <ChakraRadioCard.Item
      flex={1}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      direction="column"
      border="1px solid rgba(23, 23, 23, 0.20)"
      borderRadius="10px"
      backdropFilter="blur(27px)"
      alignItems="center"
      gap={3}
      overflow="hidden"
      justifyContent="center"
      _checked={{
        bg: 'rgba(60, 75, 211, 0.10)',
        border: '2px solid var(--Purple, #3C4BD3)',
      }}
      {...rest}
    >
      <ChakraRadioCard.ItemHiddenInput disabled={isDisabled} />
      <ChakraRadioCard.ItemControl
        flexDirection="column"
        alignItems="center"
        py={3}
        px={2}
        opacity={isDisabled ? 0.5 : 1}
      >
        {icon}
        <Text textStyle="bodySmall" textAlign="center">
          {label}
        </Text>
        {description && (
          <Text textStyle="bodyXSmall" textAlign="center" opacity={0.6}>
            {description}
          </Text>
        )}
      </ChakraRadioCard.ItemControl>
    </ChakraRadioCard.Item>
  )
}
